import React, { useState } from "react";
import { useCurrency } from '../../components/CurrencyContext';
import { Valuation } from '../../models/Valuation';
import './ValuationsGrid.css';
import { IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button } from "@mui/material";

import EditDocumentIcon from '@mui/icons-material/EditDocument';
import DeleteIcon from "@mui/icons-material/Delete";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";


interface ValuationGridProps {
    valuations: Valuation[] | null;
    onEdit: (valuation: Valuation) => void;
    onDelete: (valuation: Valuation) => void;
}

export const ValuationGrid: React.FC<ValuationGridProps> = ({ valuations, onEdit, onDelete }) => {

    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const [conversionRates, setConversionRates] = useState<{ [key: string]: number } | null>(null);
    const { currency } = useCurrency();

    const [deletingValuation, setDeletingValuation] = useState<Valuation | null>(null);

    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const confirmDelete = (valuation: Valuation) => {
        setDeletingValuation(valuation);
        setConfirmOpen(true);
    };

    const doDelete = async () => {
        setConfirmOpen(false);

        if (deletingValuation) {
            onDelete(deletingValuation);
        }
    };


    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const exchangeRatesReq = useSafeRequest<any>(api, { maxAttempts: 3 });

    const getExhangeData = async () => {
        const tasks = [
            exchangeRatesReq.run({ method: "GET", url: `exchangerates/${currency}` }),

            //Other data requirements...
        ];

        const [e] = await Promise.allSettled(tasks);

        if (e.status === "fulfilled") setConversionRates(e.value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatAmount = (amount: number, currencyCode: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    };

    const renderCurrentBaseValue = (v: Valuation) => {

        if (conversionRates) {
            const fromRate = conversionRates[v.currencyCode] || 1;
            const toRate = conversionRates[currency] || 1;
            const convertedLowValue = (v.lowerRangeAmount / fromRate) * toRate;
            const convertedHighValue = (v.upperRangeAmount / fromRate) * toRate;

            if (v.valuationTypeId === 4) {
                return `${Math.round(convertedLowValue).toLocaleString()}–${Math.round(convertedHighValue).toLocaleString()} ${currency}`;
            } else {
                return `${Math.round(convertedLowValue).toLocaleString()} ${currency}`;
            }
        }
    };

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                getExhangeData().catch(() => { });


            } catch (error) {
                console.error('Failed to fetch exchange rates:', error);
            } finally {

            }
        };

        fetchData();

    }, []);

    const stillLoading = exchangeRatesReq.loading; 

    if (stillLoading) return (<div>Loading...</div>);

    return (
        <>
            <div className="valuations-grid">
                {valuations?.map((valuation) => (
                    <div key={valuation.id} className="valuation-grid-card">

                        {/* Header: Type + Actions */}
                        <div className="valuation-card-header">

                            <div className="valuation-grid-type">
                                <Chip size="small" label={valuation.valuationType} />
                            </div>

                            {canEditArtwork && (
                                <div className="valuation-grid-actions">
                                    <Tooltip title="Edit valuation">
                                        <IconButton size="small" onClick={() => onEdit(valuation)}>
                                            <EditDocumentIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Delete valuation">
                                        <IconButton size="small" onClick={() => confirmDelete(valuation)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            )}
                        </div>

                        {/* Date */}
                        <div className="valuation-grid-item valuation-grid-date">
                            {formatDate(valuation.valuationDate)}
                        </div>

                        {/* Amount */}
                        <div className="valuation-grid-item valuation-grid-amount">

                            <div className="original-grid-amount">
                                {
                                    valuation.valuationTypeId === 4
                                        ? `${valuation.lowerRangeAmount?.toLocaleString()}–${valuation.upperRangeAmount?.toLocaleString()} ${valuation.currencyCode}`
                                        : `${valuation.lowerRangeAmount?.toLocaleString()} ${valuation.currencyCode}`
                                }
                            </div>

                            <div className="converted-grid-amount">
                                {renderCurrentBaseValue(valuation)}
                            </div>

                        </div>

                    </div>
                ))}
            </div>

            {/* Delete confirm */ }
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Valuation?</DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => { setConfirmOpen(false); setDeletingValuation(null); }}>Cancel</Button>
            <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
    </Dialog>
        </>
    );
};